/* global t, safeArray, num, pkLabeled */
// PERIODEKONTROLLER — oppgavene og rapporteringsplikten
//
// Skilt fra backend-period-recon-view.jsx av plasshensyn. Begge listene er
// UTLEDET: ingen av dem er en egen liste noen fører ved siden av arbeidet.
//
// Det er hele poenget med oppgavelista. En oppgave er et tiltak på en
// avstemmingspost i et fagområde — ikke en kopi av den. Løses tiltaket, endrer
// kildeposten seg, og oppgaven forsvinner herfra av seg selv. En egen
// oppgavetabell ville betydd at samme forhold kunne stå løst ett sted og åpent
// et annet.

const pkTaskDate = (value) => (/^\d{4}-\d{2}-\d{2}$/.test(String(value || '')) ? String(value) : null);

// Prioriteten er ikke en faglig vurdering appen gjør — den er en SORTERING av
// det som allerede er kjent: er beløpet over vesentlighetsgrensen byrået selv
// har satt, og har fristen gått ut. Uten en grense finnes det ingenting å måle
// vesentlighet mot, og da sorterer fristen alene.
const pkTaskPriority = ({ amount, dueDate, materialityThreshold, today }) => {
  const material = materialityThreshold === null || materialityThreshold === undefined
    ? null
    : Math.abs(num(amount)) >= num(materialityThreshold);
  if (material === true) return 'critical';
  const due = pkTaskDate(dueDate);
  if (due && due < today) return 'high';
  return 'normal';
};

// `prioritetId` is the stable id (compared and sorted on); `prioritet` is the
// label shown to the user.
const pkPriorityLabel = (id) => {
  if (id === 'critical') return t('rcd.priority.critical', 'Kritisk');
  if (id === 'high') return t('rcd.priority.high', 'Høy');
  return t('rcd.priority.normal', 'Normal');
};
const pkPriorityFields = (input) => {
  const id = pkTaskPriority(input);
  return { prioritetId: id, prioritet: pkPriorityLabel(id) };
};

// Getters so the labels resolve at read time and follow the UI language.
const PK_TASK_STATUS_LABEL = {
  get apen() { return t('rcd.taskStatus.open', 'Åpen'); },
  get venter_kunde() { return t('rcd.taskStatus.waitingClient', 'Venter på kunde'); },
  get ma_vurderes() { return t('rcd.taskStatus.needsReview', 'Må vurderes'); },
  get lost() { return t('rcd.taskStatus.resolved', 'Løst'); },
};

const PK_TASK_STATUS_CLS = {
  apen: 'error', venter_kunde: 'info', ma_vurderes: 'warning', lost: 'success',
};

// ------------------------------------------------------------------
// Oppgaver og avvik
// ------------------------------------------------------------------
// Fire kilder, alle ekte:
//
//   1. tiltak på en linjevurdering  — kan løses herfra, og løsningen skrives
//      tilbake på kildens vurdering
//   2. tiltak på en avstemmingspost — samme, på `explainedItems`
//   3. åpen kundeforespørsel        — eies av høyrepanelet, vises bare her
//   4. bilagsinnboksen              — ikke en avstemmingspost i det hele tatt
//
// De to første er de eneste som kan markeres løst. De to siste har sitt eget
// sted å bli lukket, og en knapp her ville laget en andre sannhet.
const buildPeriodTasks = ({
  areas = [], accounts = [], requests = [], inboxCount = 0,
  materialityThreshold = null, today = new Date().toISOString().slice(0, 10),
} = {}) => {
  const labelFor = (areaKey) => areas.find((area) => area.key === areaKey)?.displayLabel || areas.find((area) => area.key === areaKey)?.label || areaKey || '';
  const tasks = [];

  for (const account of safeArray(accounts)) {
    const areaKey = account.areaKey || 'ovrig';
    if (areaKey === 'periodekontroll') continue;

    for (const [itemId, entry] of Object.entries(account.lineAssessments || {})) {
      const tiltak = String(entry?.tiltak || '').trim();
      if (!tiltak) continue;
      const status = Object.prototype.hasOwnProperty.call(PK_TASK_STATUS_LABEL, entry?.tiltakStatus) ? entry.tiltakStatus : 'apen';
      const amount = num(entry?.lossAmount ?? entry?.writeDownAmount ?? entry?.expectedAmount ?? 0);
      tasks.push({
        id: 'line:' + account.accountId + ':' + itemId,
        kind: 'line',
        accountId: account.accountId,
        itemId,
        areaKey,
        omrade: labelFor(areaKey),
        postRef: itemId,
        beskrivelse: tiltak,
        belop: amount || null,
        ansvarlig: String(entry?.tiltakAnsvarlig || '').trim(),
        frist: pkTaskDate(entry?.dueDate),
        status,
        resultat: String(entry?.tiltakResultat || '').trim(),
        resolvable: true,
        ...pkPriorityFields({ amount, dueDate: entry?.dueDate, materialityThreshold, today }),
      });
    }

    safeArray(account.explainedItems).forEach((item, index) => {
      const action = String(item?.action || '').trim();
      if (!action || action === 'Ingen') return;
      const status = item?.resolved === true ? 'lost' : 'apen';
      tasks.push({
        id: 'item:' + account.accountId + ':' + index,
        kind: 'item',
        accountId: account.accountId,
        itemIndex: index,
        areaKey,
        omrade: labelFor(areaKey),
        postRef: account.accountId + '-P' + (index + 1),
        beskrivelse: action + (item?.reason ? ' — ' + item.reason : ''),
        belop: num(item?.amount) || null,
        ansvarlig: '',
        frist: pkTaskDate(item?.dueDate),
        status,
        resultat: '',
        resolvable: true,
        ...pkPriorityFields({ amount: item?.amount, dueDate: item?.dueDate, materialityThreshold, today }),
      });
    });
  }

  for (const request of safeArray(requests)) {
    if (String(request?.status || 'sendt') !== 'sendt') continue;
    tasks.push({
      id: 'request:' + request.requestId,
      kind: 'request',
      areaKey: request.areaKey || null,
      omrade: request.areaKey ? labelFor(request.areaKey) : t('rcd.task.clientRequest', 'Kundeforespørsel'),
      postRef: request.requestId,
      beskrivelse: request.subject || t('rcd.task.docRequest', 'Forespørsel om dokumentasjon'),
      belop: null,
      ansvarlig: '',
      frist: pkTaskDate(request.dueDate),
      status: 'venter_kunde',
      resultat: '',
      resolvable: false,
      ...pkPriorityFields({ amount: 0, dueDate: request.dueDate, materialityThreshold: null, today }),
    });
  }

  if (num(inboxCount) > 0) {
    tasks.push({
      id: 'inbox',
      kind: 'inbox',
      areaKey: null,
      omrade: t('rcd.task.inbox', 'Innboks'),
      postRef: t('rcd.task.inboxRef', 'INNBOKS'),
      beskrivelse: t('rcd.task.processInbox', 'Behandle {count} uposterte dokumenter', { count: num(inboxCount) }),
      belop: null,
      ansvarlig: '',
      frist: null,
      status: 'apen',
      resultat: '',
      resolvable: false,
      prioritetId: 'normal',
      prioritet: pkPriorityLabel('normal'),
    });
  }

  const order = { critical: 0, high: 1, normal: 2 };
  return tasks.sort((a, b) => (a.status === 'lost' ? 1 : 0) - (b.status === 'lost' ? 1 : 0)
    || order[a.prioritetId] - order[b.prioritetId]
    || String(a.frist || '9999').localeCompare(String(b.frist || '9999')));
};

// ------------------------------------------------------------------
// Rapportering
// ------------------------------------------------------------------
// Bare plikter Plumme HAR en kilde til. MVA-terminens frist kommer fra
// `vatTerms.js`, den samme som periodegjennomgangen og klientoversikten bruker.
// Byråets egne periodefrister kommer fra periodedokumentets `dueDate`.
//
// A-melding, arbeidsgiveravgift og forskuddstrekk har ingen fristkilde i
// kodebasen. De vises derfor med «Frist ikke registrert» i stedet for med en
// dato appen har regnet ut selv: en frist uten kilde er verre enn ingen frist,
// fordi den ser like autoritativ ut som de som har en.
const PK_REPORT_STATUS = {
  ikke_startet: pkLabeled('rcd.reportStatus.notStarted', 'Ikke startet', { cls: 'muted' }),
  datagrunnlag_ufullstendig: pkLabeled('rcd.reportStatus.dataIncomplete', 'Datagrunnlag ufullstendig', { cls: 'error' }),
  klar_til_utarbeidelse: pkLabeled('rcd.reportStatus.readyToPrepare', 'Klar til utarbeidelse', { cls: 'warning' }),
  klar_til_innsending: pkLabeled('rcd.reportStatus.readyToSubmit', 'Klar til innsending', { cls: 'success' }),
  innsendt: pkLabeled('rcd.reportStatus.submitted', 'Innsendt', { cls: 'success' }),
  venter_periodeslutt: pkLabeled('rcd.reportStatus.waitingPeriodEnd', 'Venter på periodeslutt', { cls: 'warning' }),
};

// A function so the names resolve at call time (not at module load).
const pkPeriodReports = () => [
  { id: 'perioderapport', navn: t('rcd.report.period', 'Perioderapport til kunde') },
  { id: 'resultat', navn: t('rcd.report.result', 'Resultatrapport') },
  { id: 'balanse', navn: t('rcd.report.balance', 'Balanserapport') },
  { id: 'hovedbok', navn: t('rcd.report.ledger', 'Hovedbok') },
  { id: 'aldersfordelt', navn: t('rcd.report.aged', 'Aldersfordelt kunde- og leverandørliste') },
];

// `datagrunnlag` is the label shown to the user; `dataComplete` is the flag the
// logic and the page branch on.
const pkDataFields = (complete) => ({
  dataComplete: Boolean(complete),
  datagrunnlag: complete ? t('rcd.report.dataComplete', 'Komplett') : t('rcd.report.dataIncomplete', 'Ufullstendig'),
});

const buildPeriodReports = ({
  periodId, periodLabel, period, state, areaStatuses = {}, allAreasDone = false, responsibleName = '',
} = {}) => {
  const rows = pkPeriodReports().map((report) => ({
    id: report.id,
    navn: report.navn,
    periode: periodLabel || periodId,
    frist: period?.dueDate || null,
    fristKilde: period?.dueDate ? t('rcd.report.dueOwn', 'Byråets egen periodefrist') : t('rcd.report.dueNone', 'Ingen periodefrist er satt på perioden'),
    ...pkDataFields(allAreasDone),
    status: allAreasDone ? 'klar_til_utarbeidelse' : 'datagrunnlag_ufullstendig',
    ansvarlig: responsibleName,
  }));

  const term = period?.vatTerm || null;
  if (period?.vatRegistered !== false && term) {
    const year = Number(String(periodId || '').slice(0, 4));
    const submitted = state?.deadlinesByYear?.[String(year)]?.mvaTerms?.['term-' + term.termNumber] === true;
    const mvaStatus = areaStatuses.mva === 'ok' ? 'klar_til_innsending' : 'datagrunnlag_ufullstendig';
    rows.push({
      id: 'mva',
      navn: t('rcd.report.vat', 'MVA-melding'),
      periode: t('rcd.report.vatTerm', 'Termin {term} {year}', { term: term.termNumber, year }),
      frist: term.dueDate || null,
      fristKilde: t('rcd.report.dueVatModel', 'Beregnet av MVA-terminmodellen'),
      ...pkDataFields(areaStatuses.mva === 'ok'),
      // Terminen kan ikke ferdigstilles før den er over. Det er ikke en mangel
      // ved arbeidet — det er en egenskap ved terminen.
      status: submitted ? 'innsendt' : (term.isTermEnd ? mvaStatus : 'venter_periodeslutt'),
      ansvarlig: responsibleName,
    });
  }

  // Lønnspliktene vises bare når det faktisk er kjørt lønn i perioden.
  const runs = safeArray(state?.payrollRuns).filter((run) => String(run?.payoutDate || run?.date || '').slice(0, 7) === String(periodId));
  if (runs.length) {
    for (const plikt of [
      { id: 'amelding', navn: t('rcd.report.aMelding', 'A-melding') },
      { id: 'forskuddstrekk', navn: t('rcd.report.withholding', 'Forskuddstrekk og arbeidsgiveravgift') },
    ]) {
      rows.push({
        id: plikt.id,
        navn: plikt.navn,
        periode: periodLabel || periodId,
        frist: null,
        fristKilde: t('rcd.report.dueNotRegistered', 'Frist ikke registrert i Plumme — følges opp i lønnsflaten'),
        ...pkDataFields(areaStatuses.lonn === 'ok'),
        status: areaStatuses.lonn === 'ok' ? 'klar_til_innsending' : 'datagrunnlag_ufullstendig',
        ansvarlig: responsibleName,
      });
    }
  }

  return rows;
};

Object.assign(window, {
  buildPeriodTasks, buildPeriodReports,
  PK_TASK_STATUS_LABEL, PK_TASK_STATUS_CLS, PK_REPORT_STATUS,
});
